import React from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CampaignConstants from '../constants';
import { ICampaignType } from 'types/campaign';
import { useTheme } from '@mui/material/styles';
import CampaignStatus from './campaign-status';

const CampaignDataGridColumnDefinition = (
    onClickGridViewIconButton: (row: ICampaignType) => void,
    onClickComputerIconButton: (row: ICampaignType) => void,
    onClickChartIconButton: (row: ICampaignType) => void,
    onClickCopyIconButton: (row: ICampaignType) => void,
    onClickTrashIconButton: (row: ICampaignType) => void
) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const iconSx = { fontSize: 22, color: 'var(--color-primary)' };
    const columnsDefinitions: GridColDef[] = [
        {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            width: 45,
        },
        {
            field: 'campaign',
            headerName: t('page.campaign.table.header.campaign'),
            flex: 1,
            renderCell: (params) => (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    sx={{
                        pr: 2,
                        textWrap: 'wrap',
                        overflow: 'hidden',
                        minHeight: '100%',
                    }}
                >
                    <Tooltip title={params.row.campaign} arrow placement="top">
                        <Typography
                            fontWeight={600}
                            fontSize={14}
                            lineHeight="17px"
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.campaign}
                        </Typography>
                    </Tooltip>

                    <Typography fontSize={14} lineHeight="17px">
                        {params.row.questions}{' '}
                        {t('page.campaign.table.suffix_questions')}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'campaignKind',
            headerName: t('page.campaign.table.header.campaignKind'),
            flex: 1,
            renderCell: (params) => (
                <Box component="div">
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.campaignKind}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'collectedAmount',
            headerName: t('page.campaign.table.header.collectedAmount'),
            flex: 1,
            renderCell: (params) => (
                <Box component="div">
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.collectedAmount}/
                        <Typography
                            component="strong"
                            fontWeight="600"
                            fontSize={'14px'}
                            lineHeight={'17px'}
                        >
                            {params.row.totalAmount}
                        </Typography>
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'timeToCollect',
            headerName: t('page.campaign.table.header.timeToCollect'),
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    height={'100%'}
                >
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                        width={'fit-content'}
                        textAlign={'center'}
                    >
                        {params.row.timeToCollect} <br />
                        <Typography
                            component="span"
                            fontSize={'14px'}
                            lineHeight={'17px'}
                            color={theme.palette.warning.main}
                        >
                            {params.row.timeStatus}
                        </Typography>
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: t('page.campaign.table.header.status'),
            flex: 1,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    gap={1}
                    alignItems="center"
                    height={'100%'}
                >
                    <CampaignStatus status={params.row.status_client} />
                </Box>
            ),
        },
        {
            field: 'creator',
            headerName: t('page.campaign.table.header.creator'),
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    sx={{
                        pr: 2,
                        textWrap: 'wrap',
                        overflow: 'hidden',
                        minHeight: '100%',
                    }}
                >
                    <Tooltip title={params.row.creator} arrow placement="top">
                        <Typography
                            component="span"
                            fontSize={'14px'}
                            lineHeight={'17px'}
                            color={theme.palette.primary.main}
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.creator}
                        </Typography>
                    </Tooltip>
                </Box>
            ),
        },
        {
            field: 'createdDate',
            headerName: t('page.campaign.table.header.createdDate'),
            width: 155,
            renderCell: (params) => (
                <Box component="div">
                    <Typography
                        component="span"
                        fontSize={'14px'}
                        lineHeight={'17px'}
                    >
                        {params.row.createdDate}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: t('page.campaign.table.header.actions'),
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box
                    component="div"
                    display="flex"
                    alignItems="center"
                    gap={'12px'}
                    height={'100%'}
                >
                    <Tooltip title="Tổng quan">
                        <IconButton
                            sx={{ padding: 0 }}
                            onClick={() =>
                                onClickGridViewIconButton(params.row)
                            }
                        >
                            <GridViewOutlinedIcon sx={iconSx} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Thiết kế">
                        <IconButton
                            sx={{ padding: 0 }}
                            onClick={() =>
                                onClickComputerIconButton(params.row)
                            }
                        >
                            <DesktopWindowsOutlinedIcon sx={iconSx} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Phân tích">
                        <IconButton
                            sx={{ padding: 0 }}
                            onClick={() => onClickChartIconButton(params.row)}
                        >
                            <BarChartOutlinedIcon sx={iconSx} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Sao chép">
                        <IconButton
                            sx={{ padding: 0 }}
                            onClick={() => onClickCopyIconButton(params.row)}
                        >
                            <ContentCopyOutlinedIcon sx={iconSx} />
                        </IconButton>
                    </Tooltip>
                    {[
                        CampaignConstants.status.TO_DO,
                        CampaignConstants.status.CLIENT_DRAFT,
                    ].includes(params.row.status_client) && (
                        <Tooltip title="Xóa">
                            <IconButton
                                sx={{ padding: 0 }}
                                onClick={() =>
                                    onClickTrashIconButton(params.row)
                                }
                            >
                                <DeleteOutlineIcon sx={iconSx} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];
    return columnsDefinitions;
};

export default CampaignDataGridColumnDefinition;
